/**
 * Фоновая проверка кодов в ЧЗ через backend (ARCH-TASK-65.12.4).
 *
 * True API нельзя вызывать синхронно в момент сканирования (лимит 2 с и запрет
 * для ККТ при рознице), поэтому касса:
 *  1) проверяет структуру кода локально (мгновенно, `datamatrix.ts`);
 *  2) ставит код в очередь (`verify`);
 *  3) получает статус асинхронно и отдаёт его в UI событием
 *     `MARKING_STATUS_UPDATED`.
 *
 * Продажа не блокируется доступностью контура: недоступность ЧЗ — это
 * `unknown`, а не отказ в обслуживании.
 */
import { CloudError, type CloudGateway } from '@core/cloud/gateway'
import type { PosDatabase } from '@core/db/db'
import type { SaleReceipt } from '@core/db/models'
import { normalizeMarkingCode } from './datamatrix'

export type MarkingStatus = 'valid' | 'invalid' | 'unknown'

/** Статусы ЧЗ «код в обороте» — только они дают `valid`. */
export const IN_CIRCULATION_STATUSES = new Set(['INTRODUCED'])

/** Статусы «код выведен из оборота» — для продажи недействителен. */
export const OUT_OF_CIRCULATION_STATUSES = new Set([
  'RETIRED',
  'WRITTEN_OFF',
  'DISPOSED',
  'UTILIZED'
])

/** Классификация статуса КМ из ответа ЧЗ. */
export function classifyMarkingStatus(status: string | undefined): MarkingStatus {
  if (!status) return 'unknown'
  if (IN_CIRCULATION_STATUSES.has(status)) return 'valid'
  if (OUT_OF_CIRCULATION_STATUSES.has(status)) return 'invalid'
  return 'unknown'
}

/** Элемент ответа backend `/cz/cises/check`. */
export interface CzCheckItem {
  code?: string
  ok?: boolean
  status?: string
  errorCode?: string
  errorMessage?: string
  /** Группа, в которой код найден (режим `auto`, 65.12.8). */
  pg?: string
}

/** Статус одного кода. */
export interface CodeVerdict {
  code: string
  status: MarkingStatus
  reason?: string
}

/** Итог по чеку. */
export interface ReceiptVerdict {
  status: MarkingStatus
  reason: string | null
}

/** Преобразовать элемент ответа backend в вердикт по коду. */
export function verdictForCode(item: CzCheckItem, fallbackCode = ''): CodeVerdict {
  const code = item.code ?? fallbackCode
  if (!item.ok) {
    if (item.errorCode === '404') {
      return { code, status: 'invalid', reason: item.errorMessage ?? `КМ/КИ не найден: ${code}` }
    }
    return {
      code,
      status: 'unknown',
      reason: item.errorMessage ?? 'Код не проверен в ЧЗ'
    }
  }

  const status = classifyMarkingStatus(item.status)
  if (status === 'valid') return { code, status }
  if (status === 'invalid') {
    return { code, status, reason: `Код ${code} в статусе ${item.status}` }
  }
  return {
    code,
    status: 'unknown',
    reason: `Код ${code} в неизвестном статусе ${item.status ?? '—'}`
  }
}

/** Итог по чеку: unknown → invalid → valid (как в backend-сверке). */
export function judgeReceiptVerdict(verdicts: CodeVerdict[]): ReceiptVerdict {
  if (verdicts.length === 0) return { status: 'unknown', reason: 'В чеке нет кодов маркировки' }

  const unchecked = verdicts.find((v) => v.status === 'unknown')
  if (unchecked) return { status: 'unknown', reason: unchecked.reason ?? 'Код не проверен' }

  const invalid = verdicts.find((v) => v.status === 'invalid')
  if (invalid) return { status: 'invalid', reason: invalid.reason ?? 'Код недействителен' }

  return { status: 'valid', reason: null }
}

/**
 * Товарная группа для запроса к ЧЗ (65.12.8).
 *
 * Касса не знает группу: один DataMatrix несёт только GTIN, а сопоставление
 * «GTIN → группа» живёт в контуре. Поэтому отправляем `auto`, а backend сам
 * перебирает группы магазина (`tenants.settings.czProductGroups`) и ставит
 * `invalid` только если код не найден ни в одной. Раньше здесь была жёсткая
 * `milk`, из-за чего коды других групп получали ложный `invalid` и блокировали
 * продажу разрешительным режимом.
 */
export const AUTO_PRODUCT_GROUP = 'auto'

/** Сколько статусов кодов держим в памяти (защита от роста за смену). */
export const MAX_CACHED_STATUSES = 5000

/** Группа для кода: контур определит её сам (см. `AUTO_PRODUCT_GROUP`). */
export function resolveProductGroup(_code: string): string {
  return AUTO_PRODUCT_GROUP
}

/** Коды маркировки чека без дублей. */
export function receiptCodes(receipt: SaleReceipt): string[] {
  const codes = new Set<string>()
  for (const item of receipt.items ?? []) {
    const code = item.datamatrix?.trim()
    if (code) codes.add(code)
  }
  return [...codes]
}

/** Коды, сгруппированные по товарной группе (для будущего мультигруппового режима). */
export function groupCodes(
  codes: string[],
  resolve: (code: string) => string = resolveProductGroup
): Map<string, string[]> {
  const groups = new Map<string, string[]>()
  for (const code of codes) {
    const pg = resolve(code)
    const list = groups.get(pg) ?? []
    list.push(code)
    groups.set(pg, list)
  }
  return groups
}

export interface CzVerifyQueueOptions {
  db: PosDatabase
  cloud: CloudGateway
  /** Куда отдавать статус кода (в плагине — событие шины для UI). */
  onStatus?: (verdict: CodeVerdict) => void
  resolvePg?: (code: string) => string
  /** Склейка быстрых сканов в один запрос, мс. */
  debounceMs?: number
  /** Сколько кодов максимум в одном запросе. */
  batchSize?: number
  /** Сколько попыток на код при сетевой ошибке. */
  maxAttempts?: number
  logger?: (message: string) => void
}

/**
 * Очередь фоновой проверки кодов.
 *
 * Два режима:
 * - `verify(codes)` — сканирование: проверяем и отдаём статус в UI;
 * - `attachReceipt(id, codes)` — чек создан: дожидаемся статусов и пишем
 *   `czStatus`/`czReason`/`czCheckedAt` в локальный чек.
 */
export class CzVerifyQueue {
  private readonly statuses = new Map<string, CodeVerdict>()
  private readonly pending = new Set<string>()
  private readonly waiters = new Map<string, Set<string>>()
  private readonly attempts = new Map<string, number>()
  /** 65.14: сколько раз код получал unknown и когда пробовать снова. */
  private readonly unknownAttempts = new Map<string, number>()
  private readonly unknownRetryAt = new Map<string, number>()
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private intervalTimer: ReturnType<typeof setInterval> | null = null
  private flushing = false

  constructor(private readonly options: CzVerifyQueueOptions) {}

  /**
   * Поставить коды в проверку. Не блокирует вызывающего.
   *
   * Код нормализуется так же, как при записи в чек (регистр и GS сохраняются,
   * переводы строк сканера убираются) — иначе бейдж в UI не найдёт статус.
   */
  verify(codes: string[]): void {
    let added = false
    for (const raw of codes) {
      const code = normalizeMarkingCode(raw)
      if (!code || this.pending.has(code)) continue
      const known = this.statuses.get(code)
      if (known && known.status !== 'unknown') continue
      if (known?.status === 'unknown') {
        // Не долбим контур на каждый скан: ждём backoff или ручного retry.
        const retryAt = this.unknownRetryAt.get(code) ?? 0
        if (retryAt > Date.now()) continue
        this.statuses.delete(code)
      }
      this.pending.add(code)
      added = true
    }
    if (added) this.schedule()
  }

  /** Привязать чек к статусам кодов и, если всё известно, записать итог. */
  async attachReceipt(receiptId: string, codes: string[]): Promise<void> {
    const unresolved: string[] = []
    for (const raw of codes) {
      const code = normalizeMarkingCode(raw)
      if (!code) continue
      const known = this.statuses.get(code)
      if (known && known.status !== 'unknown') continue
      unresolved.push(code)
      const set = this.waiters.get(code) ?? new Set<string>()
      set.add(receiptId)
      this.waiters.set(code, set)
    }
    if (unresolved.length > 0) this.verify(unresolved)
    await this.resolveReceipt(receiptId)
  }

  /** Восстановление после перезапуска: чеки без статуса ЧЗ. */
  async restore(): Promise<number> {
    const receipts = await this.options.db.receipts
      .filter(
        (receipt) =>
          (!receipt.czStatus || receipt.czStatus === 'unknown') &&
          receiptCodes(receipt).length > 0 &&
          receipt.status !== 'voided' &&
          !receipt.trash
      )
      .toArray()

    for (const receipt of receipts) {
      await this.attachReceipt(receipt.receiptId, receiptCodes(receipt))
    }
    return receipts.length
  }

  /** Отправить накопленные коды в backend (по группам). */
  async flush(): Promise<void> {
    if (this.flushing || this.pending.size === 0) return
    this.flushing = true
    try {
      const batch = [...this.pending].slice(0, this.options.batchSize ?? 100)
      const groups = groupCodes(batch, this.options.resolvePg)
      for (const [pg, codes] of groups) {
        await this.sendGroup(pg, codes)
      }
    } finally {
      this.flushing = false
    }
    await this.resolveAllWaiters()
    // Сканы, пришедшие во время запроса, не должны ждать минутного тика.
    if (this.pending.size > 0) this.schedule()
  }

  /** Отправить одну группу кодов в backend. */
  private async sendGroup(pg: string, codes: string[]): Promise<void> {
    let items: CzCheckItem[] = []
    try {
      const response = await this.options.cloud.request('/cz/cises/check', {
        method: 'POST',
        body: { pg, codes }
      })
      const body = response.body as
        | { results?: CzCheckItem[]; error?: string }
        | CzCheckItem[]
        | null

      if (Array.isArray(body)) {
        items = body
      } else if (Array.isArray(body?.results)) {
        items = body.results
      } else {
        // 401/403 — нет сессии/прав: не фиксируем коды, повторим после входа.
        if (response.status === 401 || response.status === 403) {
          throw new CloudError('Требуется вход в облако', 'not_connected')
        }
        if (response.status >= 500) {
          throw new CloudError(`HTTP ${response.status} от backend`, 'network')
        }
        // 400 и прочее: сеть ни при чём, но статуса нет — отдаём причину в UI.
        const message = body?.error ?? `HTTP ${response.status} от backend`
        for (const code of codes) {
          this.accept({ code, status: 'unknown', reason: message })
        }
        return
      }
    } catch (error) {
      this.failBatch(codes, error)
      return
    }

    for (const code of codes) {
      const item = items.find((entry) => entry.code === code) ?? {
        code,
        ok: false,
        errorCode: 'empty',
        errorMessage: 'Контур не вернул статус кода'
      }
      this.accept(verdictForCode(item, code))
    }
  }

  /** Зафиксировать вердикт и уведомить UI. */
  private accept(verdict: CodeVerdict): void {
    this.statuses.set(verdict.code, verdict)
    if (verdict.status === 'unknown') {
      const attempt = (this.unknownAttempts.get(verdict.code) ?? 0) + 1
      this.unknownAttempts.set(verdict.code, attempt)
      // 1 мин → 2 → 4 → … до 30 мин; после ручного retry счётчик сбрасывается.
      const delay = Math.min(30 * 60_000, 60_000 * 2 ** Math.min(5, attempt - 1))
      this.unknownRetryAt.set(verdict.code, Date.now() + delay)
    } else {
      this.unknownAttempts.delete(verdict.code)
      this.unknownRetryAt.delete(verdict.code)
    }
    // Кэш статусов не должен расти бесконечно за длинную смену.
    if (this.statuses.size > MAX_CACHED_STATUSES) {
      const oldest = this.statuses.keys().next().value
      if (oldest !== undefined) this.statuses.delete(oldest)
    }
    this.pending.delete(verdict.code)
    this.attempts.delete(verdict.code)
    this.options.onStatus?.(verdict)
  }

  /**
   * Повторить проверку `unknown`-кодов: либо явно переданных кассиром, либо
   * всех, у которых истёк backoff. Сами чеки перепривязываются через restore.
   */
  retry(codes?: string[]): void {
    const now = Date.now()
    const wanted = codes
      ? codes.map((raw) => normalizeMarkingCode(raw)).filter(Boolean)
      : [...this.statuses.entries()]
          .filter(([code, verdict]) => {
            if (verdict.status !== 'unknown') return false
            return (this.unknownRetryAt.get(code) ?? 0) <= now
          })
          .map(([code]) => code)

    let queued = false
    for (const code of wanted) {
      this.statuses.delete(code)
      this.unknownRetryAt.delete(code)
      if (codes) this.unknownAttempts.delete(code)
      if (!this.pending.has(code)) {
        this.pending.add(code)
        queued = true
      }
    }
    if (!queued) return
    this.schedule()
    // Возвращаем ожидание всем чекам с этими кодами, включая invalid:
    // restore() смотрит только на czStatus null/unknown и их бы пропустил.
    void this.reattachReceipts(wanted).catch((error) =>
      this.options.logger?.(`[ru-cz] retry reattach не удался: ${String(error)}`)
    )
  }

  /** Перепривязать чеки к повторно проверяемым кодам (включая invalid). */
  private async reattachReceipts(codes: string[]): Promise<void> {
    const wanted = new Set(codes)
    if (wanted.size === 0) return
    const receipts = await this.options.db.receipts
      .filter(
        (receipt) =>
          receipt.status !== 'voided' &&
          !receipt.trash &&
          receiptCodes(receipt).some((code) => wanted.has(code))
      )
      .toArray()
    for (const receipt of receipts) {
      await this.attachReceipt(receipt.receiptId, receiptCodes(receipt))
    }
  }

  /** Ошибка сети: ретраи; после исчерпания — `unknown`, чтобы чек не висел вечно. */
  private failBatch(codes: string[], error: unknown): void {
    const message = error instanceof Error ? error.message : 'Сеть недоступна'
    this.options.logger?.(`[ru-cz] проверка кодов не удалась: ${message}`)

    // Нет сессии/прав: коды не фиксируем, попытки не тратим — ждём вход в облако.
    if (error instanceof CloudError && error.kind === 'not_connected') return

    const maxAttempts = Math.max(1, this.options.maxAttempts ?? 3)
    for (const code of codes) {
      const attempt = (this.attempts.get(code) ?? 0) + 1
      this.attempts.set(code, attempt)
      if (attempt >= maxAttempts) {
        this.accept({
          code,
          status: 'unknown',
          reason: `Не удалось проверить код: ${message}`
        })
      }
    }
  }

  private schedule(): void {
    if (this.debounceTimer) return
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      void this.flush()
    }, this.options.debounceMs ?? 300)
  }

  /** Записать итог чека, если статусы всех его кодов уже известны. */
  private async resolveReceipt(receiptId: string): Promise<void> {
    const receipt = await this.options.db.receipts
      .where('receiptId')
      .equals(receiptId)
      .first()
    if (!receipt) return

    const codes = receiptCodes(receipt)
    if (codes.length === 0) return

    const verdicts: CodeVerdict[] = []
    for (const code of codes) {
      const verdict = this.statuses.get(code)
      if (!verdict) return // ещё проверяется
      verdicts.push(verdict)
    }

    const summary = judgeReceiptVerdict(verdicts)
    const byCode = new Map(verdicts.map((v) => [v.code, v]))
    const patch: Partial<SaleReceipt> = {
      czStatus: summary.status,
      czCheckedAt: Date.now(),
      // Статус каждой позиции нужен для UI и безопасной замены non_fiscal.
      items: receipt.items.map((item) => {
        const code = item.datamatrix?.trim()
        const verdict = code ? byCode.get(code) : undefined
        return verdict
          ? { ...item, czStatus: verdict.status }
          : item
      })
    }
    if (summary.reason) patch.czReason = summary.reason

    // B3/65.14: non_fiscal с непроверенными кодами после `valid` становится
    // задачей на замену фискальным чеком (деньги двигает кассир по кнопке).
    if (
      receipt.status === 'non_fiscal' &&
      (receipt.replacementState === 'awaiting_verification' ||
        receipt.replacementState === 'failed') &&
      summary.status === 'valid'
    ) {
      patch.replacementState = 'awaiting_print'
      patch.markingPrintState = 'ready'
    }
    if (
      receipt.status === 'non_fiscal' &&
      receipt.replacementState === 'awaiting_verification' &&
      summary.status === 'invalid'
    ) {
      patch.replacementState = 'failed'
      patch.replacementError = summary.reason ?? 'Код недействителен'
      patch.markingPrintState = 'failed'
    }
    // Hold: valid разрешает печать, invalid — фиксируем сбой.
    if (receipt.status === 'marking_review') {
      if (summary.status === 'valid') {
        patch.markingPrintState = 'ready'
      } else if (summary.status === 'invalid') {
        patch.markingPrintState = 'failed'
      }
    }

    await this.options.db.receipts.update(receipt.id as number, patch)

    for (const code of codes) {
      this.waiters.get(code)?.delete(receiptId)
    }
  }

  private async resolveAllWaiters(): Promise<void> {
    for (const [code, receipts] of this.waiters) {
      if (!this.statuses.has(code)) continue
      for (const receiptId of receipts) {
        await this.resolveReceipt(receiptId)
      }
    }
  }

  /** Периодическая проверка (офлайн-очередь, восстановление). */
  start(intervalMs = 60_000): void {
    if (this.intervalTimer) return
    this.intervalTimer = setInterval(() => {
      this.retry()
      void this.flush()
    }, intervalMs)
  }

  stop(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.intervalTimer) clearInterval(this.intervalTimer)
    this.debounceTimer = null
    this.intervalTimer = null
  }
}

